import { Sparkles, Paperclip, Loader2, Inbox, X } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { SlimSidebar } from '../layout/SlimSidebar';
import { MailViewer } from '../layout/MailViewer';
import { HelpHint } from '../ui/HelpHint';
import { useMemo } from 'react';
import { useFeed, type FeedItem } from '../../hooks/useFeed';
import { useToReply } from '../../hooks/useToReply';
import { useMailStore } from '../../store/mail';

/**
 * Vista Feed AI (home, default).
 * Card COMPATTE (mittente, oggetto, data, tag) degli ultimi 15 giorni.
 * Al passaggio del mouse mostra un'anteprima con il riassunto AI (fallback snippet).
 * Click su una card → dettaglio nel MailViewer (riuso store mail + useMessage).
 */
export function FeedView() {
  const { data: items, isLoading } = useFeed(15);
  const selectedMessageId = useMailStore((s) => s.selectedMessageId);
  const feedFilter = useMailStore((s) => s.feedFilter);
  const setFeedFilter = useMailStore((s) => s.setFeedFilter);
  const { data: toReply } = useToReply();

  // Applica il filtro attivo (tag o "da rispondere") sulle card del Feed.
  const filtered = useMemo(() => {
    if (!items) return items;
    if (feedFilter.kind === 'tag')
      return items.filter((it) => feedFilter.tags.some((sel) => it.tags.some((t) => t.id === sel.tagId)));
    if (feedFilter.kind === 'category')
      return items.filter((it) => it.ai_category === feedFilter.category);
    if (feedFilter.kind === 'toReply') {
      const ids = new Set((toReply ?? []).map((m) => m.id));
      return items.filter((it) => ids.has(it.id));
    }
    return items;
  }, [items, feedFilter, toReply]);

  return (
    <div className="flex flex-1 overflow-hidden">
      <SlimSidebar />

      {/* Colonna feed */}
      <section className="flex w-[460px] shrink-0 flex-col border-r border-zinc-800/80 bg-zinc-950/20">
        <div className="flex h-10 items-center gap-2 border-b border-zinc-800/80 px-4 text-xs">
          <Sparkles size={12} className="text-accent-400" />
          <span className="font-medium text-zinc-200">Feed AI</span>
          {/* Aiuto contestuale della sezione Feed */}
          <HelpHint topic="feed" />
          <span className="text-zinc-600">·</span>
          {feedFilter.kind === 'none' ? (
            <span className="text-zinc-500">ultimi 15 giorni</span>
          ) : (
            <button
              type="button"
              onClick={() => setFeedFilter({ kind: 'none' })}
              title="Rimuovi filtro"
              className="inline-flex items-center gap-1 rounded-full bg-zinc-800/80 px-2 py-0.5 text-[11px] font-medium text-zinc-200 transition hover:bg-zinc-700"
            >
              {feedFilter.kind === 'toReply' ? (
                'Da rispondere'
              ) : feedFilter.kind === 'category' ? (
                <span className="capitalize">{feedFilter.category}</span>
              ) : feedFilter.kind === 'tag' ? (
                <>
                  <span
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: feedFilter.tags[0]?.color }}
                  />
                  {feedFilter.tags.map((t) => t.label).join(' + ')}
                </>
              ) : null}
              <X size={11} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={16} className="animate-spin text-zinc-600" />
            </div>
          ) : !filtered || filtered.length === 0 ? (
            <EmptyFeed filtered={feedFilter.kind !== 'none'} onClear={() => setFeedFilter({ kind: 'none' })} />
          ) : (
            <ul className="space-y-2">
              {filtered.map((it) => (
                <FeedCard
                  key={it.id}
                  item={it}
                  active={selectedMessageId === it.id}
                />
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Dettaglio messaggio */}
      <MailViewer />
    </div>
  );
}

function FeedCard({ item, active }: { item: FeedItem; active: boolean }) {
  const setSelectedMessage = useMailStore((s) => s.setSelectedMessage);
  const sender = item.from_name ?? item.from_email ?? '(sconosciuto)';
  // Testo dell'anteprima mostrata al hover: riassunto AI o, in fallback, lo snippet.
  const preview = item.ai_summary ?? item.snippet ?? null;

  return (
    // `group` + `relative`: l'anteprima appare in hover posizionata rispetto alla card.
    <li className="group relative">
      <button
        onClick={() => setSelectedMessage(item.id)}
        className={
          'w-full rounded-lg border p-3 text-left transition ' +
          (active
            ? 'border-accent-500/40 bg-accent-500/10'
            : item.is_seen
              ? 'border-zinc-800/80 bg-zinc-900/30 hover:border-zinc-700 hover:bg-zinc-900/60'
              : 'border-zinc-700/70 bg-zinc-900/50 hover:border-zinc-600 hover:bg-zinc-900/70')
        }
      >
        {/* Riga mittente + data */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={
              'truncate text-sm ' +
              (item.is_seen ? 'font-medium text-zinc-200' : 'font-semibold text-zinc-100')
            }
          >
            {sender}
          </span>
          <span className="shrink-0 text-[10px] text-zinc-500">
            {item.date ? format(new Date(item.date), 'dd MMM', { locale: it }) : ''}
          </span>
        </div>

        {/* Oggetto + indicatori (non letto / allegato) */}
        <div className="mt-0.5 flex items-center gap-1.5">
          {!item.is_seen && (
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent-400" />
          )}
          {item.has_attachments && <Paperclip size={10} className="shrink-0 text-zinc-500" />}
          <span
            className={
              'truncate text-xs ' + (item.is_seen ? 'text-zinc-400' : 'text-zinc-200')
            }
          >
            {item.subject ?? '(senza oggetto)'}
          </span>
        </div>

        {/* Chip: categoria AI + tag colorati (il riassunto NON è più sempre visibile) */}
        {(item.ai_category || item.tags.length > 0) && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {item.ai_category && (
              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-800/80 px-2 py-0.5 text-[9px] font-medium uppercase tracking-wider text-zinc-400">
                <Sparkles size={8} />
                {item.ai_category}
              </span>
            )}
            {item.tags.map((t) => (
              <span
                key={t.id}
                title={t.name}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-medium"
                style={{ backgroundColor: `${t.color}22`, color: t.color }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: t.color }} />
                {t.name}
              </span>
            ))}
          </div>
        )}
      </button>

      {/* Anteprima al hover: riassunto AI (fallback snippet).
          `pointer-events-none` così non intercetta il click sulla card sottostante.
          Resta entro la larghezza della colonna (left/right) per non essere tagliata
          dall'overflow orizzontale del contenitore scrollabile. */}
      {preview && (
        <div className="pointer-events-none absolute left-2 right-2 top-full z-30 mt-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-3 shadow-xl ring-1 ring-black/20">
            <div className="mb-1 flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider text-accent-400">
              <Sparkles size={9} /> {item.ai_summary ? 'Riassunto AI' : 'Anteprima'}
            </div>
            <p className="line-clamp-4 text-[12px] leading-relaxed text-zinc-300">{preview}</p>
          </div>
        </div>
      )}
    </li>
  );
}

function EmptyFeed({ filtered, onClear }: { filtered?: boolean; onClear?: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 py-16 text-center">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-zinc-900 ring-1 ring-zinc-800">
        <Inbox size={20} className="text-zinc-600" />
      </div>
      {filtered ? (
        <>
          <h3 className="text-sm font-medium text-zinc-300">Nessuna mail per questo filtro</h3>
          <p className="mt-1 max-w-xs text-xs text-zinc-500">
            Nessun messaggio degli ultimi 15 giorni corrisponde al filtro selezionato.
          </p>
          <button
            type="button"
            onClick={onClear}
            className="mt-3 rounded-md border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-300 transition hover:bg-zinc-800/60"
          >
            Rimuovi filtro
          </button>
        </>
      ) : (
        <>
          <h3 className="text-sm font-medium text-zinc-300">Il tuo feed è tranquillo</h3>
          <p className="mt-1 max-w-xs text-xs text-zinc-500">
            Non ci sono messaggi recenti da mostrare. Le nuove mail appariranno qui con riassunto
            e tag generati dall'AI.
          </p>
        </>
      )}
    </div>
  );
}
